import React, {useMemo} from "react";

import {useTexture} from "@react-three/drei";
import {useFrame} from "@react-three/fiber";
import {Vec2} from "@repo/shared";
import {Color} from "three";

import {useLazyCallbackUser} from "~/api/hooks/useUser";
import {useGame} from "~/contexts/game-manager/GameManagerProvider";
import HueShiftMaterial from "~/three/effects/trail/HueShiftMaterial";

import {ParticleEffect, ParticleEffectRef} from "../particle-effect";

const maxParticleAmount = 800;
const zIndex = 50;

/**
 * Renders particle trails for any player who is actively being affected by an attractor.
 * This component is driven entirely by the central GameManager.
 */
const ParticleTrails: React.FC = () => {
  const {gameManager} = useGame();
  const particleTexture = useTexture("/images/particle_slim.png");
  const particleEffectRef = React.useRef<ParticleEffectRef>(null);

  // Your optimized hook for getting user data.
  const lookupUser = useLazyCallbackUser();

  // A reusable vector to avoid creating new objects in the render loop.
  const interpolatedPosition = useMemo(() => new Vec2(0, 0), []);

  useFrame(() => {
    if (!gameManager) return;

    const allPlayers = gameManager.getAllPlayers();
    if (allPlayers.length === 0) return;

    for (const renderablePlayer of allPlayers) {
      const {player, smoothPlayerPosition} = renderablePlayer;

      // The condition for emitting a trail particle is when the simulation
      // has set the player's `attractionActive` flag to true for this step.
      if (player.attractionActive && !player.isDead && !player.isCompleted) {
        // 1. Look up the user info directly. Since this is optimized, no local cache is needed.
        const userInfo = lookupUser({id: player.ownerId});
        const playerColor = new Color(userInfo?.color || "#ffffff"); // Default to white
        playerColor.offsetHSL(player.instanceId / 3, 0, 0);

        // 2. Calculate the smooth interpolated position.
        interpolatedPosition.copyFrom(smoothPlayerPosition);

        // 3. Emit the particle with the correct position and color.
        particleEffectRef.current?.emit({
          x: interpolatedPosition.x,
          y: interpolatedPosition.y,
          count: 1,
          color: playerColor,
        });
      }
    }
  });

  return (
    <ParticleEffect
      ref={particleEffectRef}
      sizeCurve={[
        {stop: 0, size: 0.75},
        {stop: 1, size: 0.15},
      ]}
      colorCurve={[
        // The color is now passed in the `emit` call, so this is just a default.
        {stop: 0, color: new Color("#ffffff")},
      ]}
      particleLifetime={0.8}
      maxParticles={maxParticleAmount}
      zIndex={zIndex}
      gravity={0.1}
      velocityMultiplier={0}
      upwardBias={0.0}
    >
      <planeGeometry args={[1, 1]} />
      <HueShiftMaterial map={particleTexture} />
    </ParticleEffect>
  );
};

export default ParticleTrails;
